import React, { forwardRef } from 'react';
import { cn } from '@/lib/utils';
import RewardCoin from '@/components/Icons/RewardCoin';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Tier, getTier, TIER_HEX } from '@/lib/points';

interface ProfilePictureProps extends React.HTMLAttributes<HTMLDivElement> {
  src?: string;
  alt?: string;
  className?: string;
  tier?: Tier;
  points?: number;
  username?: string;
  variant?: 'pill' | 'badge' | 'none';
  size?: 'sm' | 'md';
}

const ONEFINITY_OFFICIAL_USERNAME = 'OnefinityCNC';
const ONEFINITY_GREEN_HEX = '#2DBE60';

const BADGE_SIZE_CLASSES: Record<'sm' | 'md', { wrap: string; text: string }> =
  {
    sm: { wrap: 'h-[14px] px-1 -bottom-1', text: 'text-[10px]' },
    md: { wrap: 'h-[18px] px-1.5 -bottom-1.5', text: 'text-[11px]' },
  };

const TIER_LABELS: Record<Tier, string> = {
  bronze: 'Bronze',
  silver: 'Silver',
  gold: 'Gold',
  platinum: 'Platinum',
};

const ProfilePicture = forwardRef<HTMLDivElement, ProfilePictureProps>(
  (
    {
      className,
      src,
      alt = 'U',
      tier,
      points,
      username,
      variant = 'none',
      size = 'md',
      ...props
    },
    ref
  ) => {
    const badgeSize = BADGE_SIZE_CLASSES[size];
    const resolvedTier = points !== undefined ? getTier(points) : tier;
    const showTier = resolvedTier !== undefined && points !== undefined;

    const isOnefinityOfficialBadge =
      variant === 'badge' && username === ONEFINITY_OFFICIAL_USERNAME;
    const tierColor = isOnefinityOfficialBadge
      ? ONEFINITY_GREEN_HEX
      : resolvedTier
        ? TIER_HEX[resolvedTier]
        : undefined;

    const avatar = (
      <Avatar
        className={cn(
          'w-[40px] h-[40px] object-cover rounded-full bg-gray-500 flex items-center justify-center flex-shrink-0 relative z-10',
          className
        )}
        style={
          isOnefinityOfficialBadge || (showTier && variant !== 'none')
            ? { border: `2.5px solid ${tierColor}` }
            : { border: '1.5px solid #9ca3af' }
        }
        {...(variant === 'none' ? { ref, ...props } : {})}
      >
        <AvatarImage
          className="rounded-full w-full h-full"
          src={src}
          alt={alt}
        />
        <AvatarFallback className="text-lg font-medium">
          {alt.charAt(0)}
        </AvatarFallback>
      </Avatar>
    );

    if (!showTier || variant === 'none') return avatar;

    if (variant === 'pill') {
      return (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div
                ref={ref}
                className="inline-flex items-center flex-shrink-0"
                {...props}
              >
                {avatar}
                <div
                  className="-ml-5 pl-7 pr-4 h-[40px] bg-white rounded-r-full flex items-center gap-2 shadow-sm"
                  style={{ border: `1px solid ${tierColor}40` }}
                >
                  <RewardCoin coinType={resolvedTier} width={22} height={22} />
                  <span
                    className="text-sm font-bold whitespace-nowrap leading-none"
                    style={{ color: tierColor }}
                  >
                    {points.toLocaleString()}
                  </span>
                </div>
              </div>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="text-xs">
              {TIER_LABELS[resolvedTier]} — {points.toLocaleString()} pts
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    }

    // badge variant
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div
              ref={ref}
              className="relative inline-flex flex-shrink-0"
              {...props}
            >
              {avatar}
              <span
                className={cn(
                  'absolute left-1/2 -translate-x-1/2 z-20 bg-white rounded-full flex items-center justify-center shadow-sm whitespace-nowrap',
                  badgeSize.wrap
                )}
                style={{ border: `1px solid ${tierColor}60` }}
              >
                <span
                  className={cn('font-bold leading-none', badgeSize.text)}
                  style={{ color: tierColor }}
                >
                  {points.toLocaleString()}
                </span>
              </span>
            </div>
          </TooltipTrigger>
          <TooltipContent side="bottom" className="text-xs">
            {TIER_LABELS[resolvedTier]} — {points.toLocaleString()} pts
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }
);

ProfilePicture.displayName = 'ProfilePicture';

export default ProfilePicture;
